import React, { useState } from 'react';
import cn from 'classnames';
import { getBroadcastRecipients } from '../../../api/noir';

// Maps a stored message's smsStatus to a short label + badge tone for the recipient drill-down.
const RECIPIENT_STATUS = {
  delivered: { label: 'Delivered', tone: 'ok' },
  sent: { label: 'Sent', tone: 'pending' },
  queued: { label: 'Sent', tone: 'pending' },
  undelivered: { label: 'Undelivered', tone: 'bad' },
  failed: { label: 'Undelivered', tone: 'bad' },
  not_sent: { label: 'Not sent', tone: 'bad' }
};

const REASON_LABEL = { no_phone: 'no phone', opted_out: 'opted out', send_error: 'send error' };

const statusLabel = (status, failureReason) => {
  switch (status) {
    case 'scheduled':
      return 'Scheduled';
    case 'cancelled':
      return 'Cancelled';
    case 'sending':
      return 'Sending';
    case 'failed':
      if (failureReason === 'all_opted_out') return 'Failed to send; all opted-out';
      if (failureReason === 'too_large') return 'Failed; cohort too large';
      if (failureReason === 'unfiltered') return 'Failed; filters matched everyone';
      return 'Failed';
    default:
      return 'Sent';
  }
};

const PastBroadcastsLog = ({ campaigns, setCampaigns, onEditBroadcast, onCancelBroadcast, onDuplicate, onRetryFailed }) => {
  const [showLog, setShowLog] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [retryingId, setRetryingId] = useState(null);

  const handleRetry = c => {
    setRetryingId(c.id);
    Promise.resolve(onRetryFailed(c)).finally(() => setRetryingId(null));
  };

  // Click a sortable column header: toggle direction if it's already active, else switch to it
  // (newest-first for date, A→Z for the text columns).
  const onSort = key => {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
  };

  const q = query.trim().toLowerCase();
  const matched = q
    ? campaigns.filter(c => [c.title, c.message].some(v => (v || '').toLowerCase().includes(q)))
    : campaigns;

  const filtered = [...matched].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'date') cmp = a.whenMs - b.whenMs;
    else if (sortKey === 'status') cmp = (a.status || '').localeCompare(b.status || '');
    else if (sortKey === 'sender') cmp = (a.sender || '').localeCompare(b.sender || '', undefined, { sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleExpand = c => {
    const next = expanded === c.id ? null : c.id;
    setExpanded(next);
    if (next && !c.recipientsLoaded) {
      getBroadcastRecipients(c.id)
        .then(data =>
          setCampaigns(p =>
            p.map(row =>
              row.id === c.id
                ? { ...row, recipientList: data.recipients, recipientCounts: data.counts, recipientsTruncated: data.truncated, recipientsLoaded: true }
                : row
            )
          )
        )
        .catch(() => {});
    }
  };

  return (
    <div className='nb-card nb-log-card'>
      <div className={cn('nb-log-header', { open: showLog })} onClick={() => setShowLog(p => !p)}>
        <span className='nb-log-title'>PAST BROADCASTS ({campaigns.length})</span>
        <span className={cn('nb-chevron', { open: showLog })}>▾</span>
      </div>

      {showLog && (campaigns.length === 0 ? (
        <p className='nb-log-empty'>No broadcasts yet.</p>
      ) : (
        <div className='nb-log-table-wrap'>
          <div className='nb-log-controls'>
            <input
              className='nb-log-search'
              type='text'
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder='Search by title or message…'
            />
          </div>
          {filtered.length === 0 ? (
            <p className='nb-log-empty'>No broadcasts match “{query.trim()}”.</p>
          ) : (
          <table className='nb-log-table'>
            <thead>
              <tr>
                {[
                  { label: '' },
                  { label: 'Status', sort: 'status' },
                  { label: 'Title' },
                  { label: 'When', sort: 'date' },
                  { label: 'Message' },
                  { label: 'Filters' },
                  { label: 'Recipients' },
                  { label: 'Sender', sort: 'sender' },
                  { label: '' }
                ].map((col, i) => (
                  <th
                    key={i}
                    className={cn({ sortable: col.sort, active: col.sort && col.sort === sortKey })}
                    onClick={col.sort ? () => onSort(col.sort) : undefined}
                  >
                    {col.label}
                    {col.sort && (
                      <span className='nb-sort-caret'>{col.sort === sortKey ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                // Rows with send results expand on click; scheduled/cancelled have nothing to show.
                const canExpand = c.status !== 'scheduled' && c.status !== 'cancelled';
                return (
                <React.Fragment key={c.id}>
                  <tr
                    className={cn('row', { expanded: expanded === c.id, clickable: canExpand })}
                    onClick={canExpand ? () => toggleExpand(c) : undefined}
                  >
                    <td className='caret-cell'>
                      {canExpand && <span className={cn('nb-row-caret', { open: expanded === c.id })}>▾</span>}
                    </td>
                    <td>
                      <span className={cn('nb-status-badge', c.status)}>{statusLabel(c.status, c.failureReason)}</span>
                      {c.status === 'sent' && c.failed > 0 && (
                        <span className='nb-status-partial'>
                          {c.failed}/{c.recipients} failed
                        </span>
                      )}
                    </td>
                    <td className='bold'>{c.title || '—'}</td>
                    <td className='muted when-cell'>{c.sentAt}</td>
                    <td className='message-cell'>
                      {c.message.slice(0, 44)}
                      {c.message.length > 44 ? '…' : ''}
                    </td>
                    <td>
                      <span className='nb-filter-chip'>{c.cohort}</span>
                    </td>
                    <td className='recipients-cell'>
                      <span className='recipients-count'>{c.recipients}</span>
                      {c.status === 'sent' && c.recipients > 0 && (
                        <span className='nb-delivered'>· {Math.round((c.delivered / c.recipients) * 100)}% delivered</span>
                      )}
                      {c.status === 'scheduled' && <span className='nb-delivered'>· est. (resolves at send)</span>}
                    </td>
                    <td className='muted sender-cell'>{c.sender}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className='nb-row-actions'>
                        {(c.status === 'scheduled' || c.status === 'failed') && (
                          <button className='nb-row-action' onClick={() => onEditBroadcast(c)}>
                            {c.status === 'failed' ? 'Edit & reschedule' : 'Edit'}
                          </button>
                        )}
                        {(c.status === 'scheduled' || c.status === 'failed' || c.status === 'sending') && (
                          <button className='nb-row-action danger' onClick={() => onCancelBroadcast(c)}>
                            {c.status === 'sending' ? 'Stop' : 'Cancel'}
                          </button>
                        )}
                        {c.status !== 'scheduled' && c.status !== 'sending' && (
                          <button className='nb-row-action' onClick={() => onDuplicate(c)}>Duplicate</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded === c.id && (
                    <tr className='expansion'>
                      <td colSpan={9} className='expansion-cell'>
                        <div className='nb-bc-detail'>
                          <div className='nb-bc-detail-row'>
                            <span className='nb-bc-label'>Title</span>
                            <span className='nb-bc-value'>{c.title || '—'}</span>
                          </div>
                          <div className='nb-bc-detail-row'>
                            <span className='nb-bc-label'>Filters</span>
                            <span className='nb-bc-value'>{c.cohort}</span>
                          </div>
                          <div className='nb-bc-detail-row'>
                            <span className='nb-bc-label'>Message</span>
                            <span className='nb-bc-value'>{c.message}</span>
                          </div>
                        </div>

                        {c.recipientCounts && (
                          <div className='nb-recipients'>
                            {(() => {
                              const retryable = (c.recipientList || []).filter(
                                r => r.status === 'not_sent' && r.reason === 'send_error'
                              ).length;
                              return (
                                <div className='nb-recipients-header'>
                                  <p className='nb-result-block-label'>Recipients</p>
                                  {retryable > 0 && (
                                    <button
                                      className='nb-row-action'
                                      disabled={retryingId === c.id}
                                      onClick={() => handleRetry(c)}
                                    >
                                      {retryingId === c.id ? 'Retrying…' : `Retry ${retryable} failed send${retryable > 1 ? 's' : ''}`}
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                            <p className='nb-recipients-summary'>
                              {c.recipientCounts.attempted} targeted · {c.recipientCounts.delivered} delivered
                              {` · ${c.recipientCounts.replied || 0} replied within 7d`}
                              {c.recipientCounts.undelivered > 0 && ` · ${c.recipientCounts.undelivered} undelivered`}
                              {c.recipientCounts.notSent > 0 && ` · ${c.recipientCounts.notSent} not sent`}
                              {c.optOuts > 0 && ` · ${c.optOuts} opted out`}
                            </p>
                            <div className='nb-recipient-list'>
                              {(c.recipientList || []).map((r, i) => {
                                const meta = RECIPIENT_STATUS[r.status] || { label: r.status, tone: 'pending' };
                                return (
                                  <div key={`${r.userId}-${r.status}-${i}`} className='nb-recipient-row'>
                                    <span className={cn('nb-recipient-status', meta.tone)}>
                                      {meta.label}
                                      {r.reason ? ` · ${REASON_LABEL[r.reason] || r.reason}` : ''}
                                    </span>
                                    <span className='nb-recipient-name'>{r.name || 'Unknown'}</span>
                                    <span className='nb-recipient-phone'>{r.phone || '—'}</span>
                                    {r.replied && r.threadId ? (
                                      <a
                                        className='nb-recipient-replied yes'
                                        href={`/noir?tab=conversations&threadId=${r.threadId}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                      >
                                        Replied ↗
                                      </a>
                                    ) : (
                                      <span className='nb-recipient-replied'>{r.status === 'not_sent' ? '' : 'No reply'}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {c.recipientsTruncated && (
                              <p className='nb-recipients-foot'>Showing the first {c.recipientList.length} of {c.recipientCounts.stored} sent.</p>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      ))}
    </div>
  );
};

export default PastBroadcastsLog;
